<script lang="ts" setup>
import { cva } from 'class-variance-authority';
import { computed, VNode } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';
import { sanitize } from '@/helpers/sanitize';

export type BaseLabelColor =
  | 'white'
  | 'primary'
  | 'secondary'
  | 'brand'
  | 'success'
  | 'tertiary'
  | 'placeholder'
  | 'warning'
  | 'error';

export type BaseLabelProps = {
  text?: string | VNode;
  class?: string | { [key: string]: boolean };
  preset?: 'h1' | 'h2' | 'h3' | 'medium' | 'small' | 'xs' | 'large';
  weight?: 'regular' | 'bold' | 'medium' | 'semi';
  color?: BaseLabelColor;
  overflow?: 'wrap' | 'ellipsis';
  capitalize?: boolean;
};

const baseLabel = cva('font-inter whitespace-pre-line', {
  variants: {
    capitalize: {
      true: 'first-letter:uppercase',
      false: '',
    },
    preset: {
      h1: 'text-3xl font-rethink-sans leading-10 font-medium',
      h2: 'text-3xl font-rethink-sans leading-9 font-medium',
      h3: 'text-2xl font-rethink-sans leading-8 font-medium',
      medium: 'text-base leading-6',
      small: 'text-sm leading-5',
      xs: 'text-xs leading-4',
      large: 'text-lg leading-6',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semi: 'font-semibold',
      bold: 'font-bold',
    },
    color: {
      primary: 'text-primary',
      white: 'text-white',
      secondary: 'text-secondary',
      brand: 'text-brand-solid',
      tertiary: 'text-tertiary',
      placeholder: 'text-placeholder',
      warning: 'text-warning-primary',
      error: 'text-error-solid',
      success: 'text-success-primary',
    },
    overflow: {
      wrap: 'text-wrap',
      ellipsis: 'text-ellipsis',
    },
  },
  compoundVariants: [],
  defaultVariants: {
    overflow: 'ellipsis',
    capitalize: true,
  },
});

const formattedText = computed(() => {
  if (!props.text || typeof props.text !== 'string') return props.text;

  const text = props.text.trim();
  const lines = text.split('\n');

  let html = '';
  const listStack = [];
  let prevWasText = false;

  const getIndentLevel = (line) => {
    const match = line.match(/^(\s*)/);
    return match ? Math.floor(match[1].length / 2) : 0;
  };

  const isListItem = (line) => /^[-*•]\s+/.test(line.trim());
  const cleanItem = (line) => line.trim().replace(/^[-*•]\s+/, '');

  lines.forEach((line) => {
    const trimmed = line.trim();
    const level = getIndentLevel(line);

    // LIGNE VIDE → spacing propre
    if (!trimmed) {
      prevWasText = false;
      while (listStack.length) {
        html += '</ul>';
        listStack.pop();
      }

      html += '<div class="my-3"></div>';
      return;
    }

    // IMAGE
    if (trimmed.startsWith('<img')) {
      prevWasText = false;
      while (listStack.length) {
        html += '</ul>';
        listStack.pop();
      }

      html += `<div class="my-4">${trimmed}</div>`;
      return;
    }

    // LISTE
    if (isListItem(line)) {
      prevWasText = false;
      const content = cleanItem(line);

      while (listStack.length < level + 1) {
        html += '<ul class="ml-5 list-disc space-y-1">';
        listStack.push('ul');
      }

      while (listStack.length > level + 1) {
        html += '</ul>';
        listStack.pop();
      }

      html += `<li>${content}</li>`;
      return;
    }

    // TEXTE NORMAL
    while (listStack.length) {
      html += '</ul>';
      listStack.pop();
    }

    if (prevWasText) html += '<br>';
    html += trimmed;
    prevWasText = true;
  });

  // 🔹 fermeture finale
  while (listStack.length) {
    html += '</ul>';
    listStack.pop();
  }

  return html;
});
const props = defineProps<BaseLabelProps>();

const classes = computed(() => cn(baseLabel(props), props.class));
</script>

<template>
  <span :class="classes" v-html="sanitize(formattedText)"></span>
</template>
